import { jsxAsElement as jsx } from 'web-shared/src/lib/jsx';
import {
  API_KEY,
  handleLastFmToken,
  initiateLastFmAuth,
  loadLastFmSession,
  SHARED_SECRET,
} from './last-fm/session';
import { LastFmScrobbleErrors } from 'web-shared/src/types/errors';
import md5 from 'md5';
import { safeFetch } from 'web-shared/src/lib/fetch';

interface Song {
  artist: string;
  album: string;
  track: string;
  timestamp: string;
}

export class LastFmScrobbler extends HTMLElement {
  session = loadLastFmSession();
  urlParams = new URLSearchParams(window.location.search);
  token = this.urlParams.get('token');
  songs: Song[] = [];

  constructor() {
    super();
  }

  scrobbleSongs = async () => {
    const chunkSize = 25;
    const chunks: Song[][] = [];
    for (let i = 0; i < this.songs.length; i += chunkSize) {
      chunks.push(this.songs.slice(i, i + chunkSize));
    }
    const payloads = chunks.map((chunk) => {
      const payload: Record<string, string> = {
        api_key: API_KEY,
        sk: `${this.session?.key}`,
        method: 'track.scrobble',
      };

      chunk.forEach((song, i) => {
        payload[`artist[${i}]`] = song.artist;
        payload[`album[${i}]`] = song.album;
        payload[`track[${i}]`] = song.track;
        payload[`timestamp[${i}]`] = song.timestamp;
      });

      const signature = Object.keys(payload)
        .sort() // ASCII based sorting is expected
        .map((key) => `${key}${payload[key]}`)
        .join('');
      payload.api_sig = md5(`${signature}${SHARED_SECRET}`);
      return payload;
    });

    this.innerHTML = '';
    this.append(
      <div>
        {this.renderHeader()}
        <p>Scrobbling {this.songs.length} songs...</p>
      </div>,
    );

    let failure = false;
    for (let payloadIndex = 0; payloadIndex < payloads.length; payloadIndex++) {
      const scrobbleResult = await safeFetch<never, LastFmScrobbleErrors>(
        'https://ws.audioscrobbler.com/2.0/?format=json',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: new URLSearchParams(payloads[payloadIndex]),
        },
      );

      if (!failure && scrobbleResult.isErr()) {
        failure = true;
      }

      this.append(
        ...chunks[payloadIndex].map((song, songIndex) => (
          <p>
            ({songIndex + 1 + payloadIndex * chunkSize}/{this.songs.length}){' '}
            {scrobbleResult.isOk() ? 'Scrobbled' : 'Failed'} {song.artist} -{' '}
            {song.track} @ {song.timestamp}
          </p>
        )),
      );
    }

    this.append(<br />);
    this.append(
      failure ? (
        <p>
          Something went wrong! please check the log output in your browser.
        </p>
      ) : (
        <p>Done! Remember to delete your .scrobbler.log file!</p>
      ),
    );
  };

  handleFile = async (file: File) => {
    const fileContent = await file.text();
    const splitContent = fileContent.split('\n');

    this.songs = splitContent
      .filter((line) => !line.startsWith('#'))
      .filter((line) => line.length > 0)
      .map((rawSong) => rawSong.split('\t'))
      .map((song) => {
        const timestamp = parseInt(song[6]);
        const offset = new Date().getTimezoneOffset();
        const actualTime = timestamp + offset * 60;

        return {
          artist: song[0],
          album: song[1],
          track: song[2],
          timestamp: actualTime.toString(),
        };
      });

    this.innerHTML = '';
    this.append(
      <div>
        {this.renderHeader()}
        <p>The following songs will be scrobbled</p>
        <ul>
          {...this.songs.map((song) => (
            <li>
              {song.artist} - {song.track} ({song.album}) @ {song.timestamp}
            </li>
          ))}
        </ul>
        <button onclick={() => this.scrobbleSongs()}>Scrobble</button>
      </div>,
    );
  };

  renderHeader = (): JSX.Element => {
    return this.session ? (
      <div>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span>Logged in as {this.session.name}</span>
          <button
            onclick={() => {
              localStorage.removeItem('lastfm-session');
              location.reload();
            }}
          >
            Logout
          </button>
        </div>
        <hr />
      </div>
    ) : (
      <div>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span>Not logged in</span>
          <button onclick={() => initiateLastFmAuth()}>Login</button>
        </div>
        <hr />
      </div>
    );
  };

  connectedCallback() {
    this.innerHTML = '';

    this.append(
      <div>
        {this.renderHeader()}
        {this.session ? (
          <input
            type="file"
            id="fileUpload"
            onchange={(event: Event) => {
              void (async () => {
                const files = (event.target as HTMLInputElement).files;

                if (files?.length === 1) {
                  await this.handleFile(files[0]);
                }
              })();
            }}
          />
        ) : (
          <input type="file" id="fileUpload" disabled={true} />
        )}
      </div>,
    );

    if (this.token) {
      void handleLastFmToken(this.token);
    }
  }
}
